import { useCallback, useEffect, useState } from 'react';
import { apiFetch } from '@/lib/api';
import type { AnalyticsResp } from '@/lib/miniprogramAnalyticsShared';

export type MiniprogramAnalyticsAction =
  | 'get_miniprogram_analytics'
  | 'site_master_get_miniprogram_analytics';

export function useMiniprogramAnalytics(
  mpId: number,
  days: number,
  autoRefreshMs = 60000,
  apiAction: MiniprogramAnalyticsAction = 'get_miniprogram_analytics'
) {
  const [data, setData] = useState<AnalyticsResp | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState('');

  const load = useCallback(
    async (silent = false) => {
      if (mpId < 1) {
        setLoading(false);
        setData(null);
        return;
      }
      if (silent) setRefreshing(true);
      else setLoading(true);
      setErr('');
      try {
        const r = await apiFetch<AnalyticsResp>(apiAction, {
          method: 'GET',
          query: { miniprogram_id: mpId, days },
        });
        if (r.ok) {
          setData(r);
        } else {
          setErr('加载统计失败');
        }
      } catch {
        setErr('加载统计失败');
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [mpId, days, apiAction]
  );

  useEffect(() => {
    void load(false);
  }, [load]);

  useEffect(() => {
    if (mpId < 1 || autoRefreshMs < 1) return;
    const timer = window.setInterval(() => void load(true), autoRefreshMs);
    return () => window.clearInterval(timer);
  }, [load, mpId, autoRefreshMs]);

  return { data, loading, refreshing, err, load };
}
